/**
 * ZCTA dot for collection picker: large tap target, no Leaflet popup (popup steals clicks on some devices).
 * - `markerSelectionMode="modal"`: click opens parent modal (two-step add).
 * - `markerSelectionMode="direct"`: click toggles list immediately (for lasso / bulk tools).
 */
import React, { useMemo } from "react";
import { Marker, Tooltip } from "react-leaflet";
import L from "leaflet";
import { useTheme } from "@mui/material/styles";

const HIT_PX = 44;
const DOT_PX = 24;

function createCollectionIcon(isSelected, theme) {
  // Match legacy ZipMarker “active” green and app theme (palette.success — #2e7d32 main)
  const color = isSelected ? theme.palette.success.main : theme.palette.primary.main;
  return L.divIcon({
    className: "zip-collection-marker-leaflet",
    html: `<div class="zip-collection-marker-hit" style="
      width:${HIT_PX}px;
      height:${HIT_PX}px;
      display:flex;
      align-items:center;
      justify-content:center;
      cursor:pointer;
      touch-action:manipulation;
    "><div style="
      width:${DOT_PX}px;
      height:${DOT_PX}px;
      background-color:${color};
      border-radius:50%;
      border:3px solid #fff;
      box-shadow:0 2px 10px rgba(0,0,0,0.35);
    "></div></div>`,
    iconSize: [HIT_PX, HIT_PX],
    iconAnchor: [HIT_PX / 2, HIT_PX / 2],
  });
}

const ZipCollectionMarker = ({
  zip,
  isSelected,
  onToggle,
  onOpenDetail,
  markerSelectionMode = "modal",
}) => {
  const theme = useTheme();
  const zipId = String(zip.id);
  const icon = useMemo(
    () => createCollectionIcon(isSelected, theme),
    [isSelected, theme]
  );

  if (!zip.lat || !zip.lng) return null;

  const tip =
    markerSelectionMode === "direct"
      ? `ZCTA ${zipId}${zip.city ? ` — ${zip.city}${zip.state ? `, ${zip.state}` : ""}` : ""} — ${
          isSelected ? "click to remove from list" : "click to add to list"
        }`
      : `ZCTA ${zipId}${zip.city ? ` — ${zip.city}${zip.state ? `, ${zip.state}` : ""}` : ""} — click for Census details and add to list`;

  return (
    <Marker
      position={[zip.lat, zip.lng]}
      icon={icon}
      eventHandlers={{
        click: (e) => {
          if (e?.originalEvent?.stopPropagation) e.originalEvent.stopPropagation();
          if (markerSelectionMode === "direct") {
            if (onToggle) onToggle(zipId);
          } else if (onOpenDetail) {
            onOpenDetail(zip);
          }
        },
      }}
    >
      <Tooltip
        direction="top"
        offset={[0, -18]}
        opacity={0.95}
        sticky
      >
        <span style={{ fontWeight: 600, fontSize: 13 }}>{tip}</span>
      </Tooltip>
    </Marker>
  );
};

export default React.memo(ZipCollectionMarker);
